import { ALLOWED_PARENT_ORIGINS } from '../constants';
import { EMessageType, IMessagePayload } from '../types';

// const ALLOWED_ORIGIN_PATTERN = IS_DEVELOPMENT_ENV
//   ? /localhost/
//   : /\.plumhq\.com$/;

/**
 * function to post message to parent window
 * @param type message type string
 * @param data message payload to be sent
 */
export const postMessageToParent = (
  type: IMessagePayload['type'],
  data: IMessagePayload['data']
) => {
  window.dev.i('postMessageToParent', type, data);
  window.dev.i('window.parent', window.parent !== window);
  if (window.parent !== window) {
    // Surface the silent-drop case: if the actual parent origin isn't in the
    // allowlist, every postMessage below targets a non-matching origin and the
    // browser drops it with no error — which looks exactly like the bug this
    // relay fixes. Log it so it's diagnosable.
    const parentOrigin =
      window.location.ancestorOrigins?.[0] || document.referrer || '';
    if (
      parentOrigin &&
      !ALLOWED_PARENT_ORIGINS.some((origin) => parentOrigin.startsWith(origin))
    ) {
      window.dev.e(
        'postMessageToParent: parent origin not in ALLOWED_PARENT_ORIGINS; message will be dropped',
        parentOrigin
      );
    }
    window.dev.i('ALLOWED_PARENT_ORIGINS', ALLOWED_PARENT_ORIGINS);
    ALLOWED_PARENT_ORIGINS.forEach((origin) => {
      window.dev.i('origin', origin);
      try {
        postMessage(type, data, window.parent, origin);
      } catch (e) {
        window.dev.e('Failed to post message to parent window', e, origin);
      }
    });
  }
};

/**
 * function to post message to parent or child iframe window
 * @param type message type string
 * @param data message payload to be sent
 * @param iframe if null, post message to parent window
 * @param origin origin of the parent window
 */
export const postMessage = (
  type: IMessagePayload['type'],
  data: IMessagePayload['data'],
  iframe: HTMLIFrameElement | null | Window,
  origin?: string
) => {
  const payload = {
    type,
    data,
  };
  const message = JSON.stringify(payload);

  window.dev.i(
    'posting the message!',
    message,
    iframe instanceof Window,
    iframe instanceof HTMLIFrameElement,
    iframe != null,
    typeof iframe,
    origin
  );

  if (iframe instanceof HTMLIFrameElement) {
    iframe?.contentWindow?.postMessage?.(message, iframe.src);
  } else if (iframe != null && origin) {
    iframe.postMessage?.(message, origin);
  }
};

/**
 * NativeBridge methods the chat asks the native shell to run. Kept as a union
 * so the parent relay can whitelist exactly these.
 */
export type TNativeBridgeMethod = 'callDeeplink';

/**
 * Invoke a NativeBridge method from the chat.
 *
 * Key off the *bridge*, not the frame: if `window.NativeBridge` exists in THIS
 * frame, call it directly. That covers both the full-page case (own WebView,
 * e.g. Support Center) AND Android iframes — Android injects the bridge into
 * every frame, so every existing embedder (claims, d2c/rtl_bot, …) keeps working
 * exactly as before, untouched.
 *
 * Only when the bridge is absent in-frame do we relay to the parent. That is the
 * iOS-iframe case (WKUserScript defaults to `forMainFrameOnly`, so the bridge is
 * injected into the top WebView only) — the cashless-letter / cult-deeplink bug.
 * The parent WebView (which has the bridge on both platforms) runs it, provided
 * that parent has an NB_CALLBACK receiver (today: claims).
 */
export const invokeNativeBridge = (
  method: TNativeBridgeMethod,
  ...args: Array<string>
) => {
  // Call as a member (this === NativeBridge) — matches every other bridge call
  // site in the repo. A detached call would leave `this` undefined inside the
  // method, which could break an iOS shim method that relies on it.
  const bridge = window?.NativeBridge as
    | Record<string, ((...a: Array<string>) => void) | undefined>
    | undefined;
  if (typeof bridge?.[method] === 'function') {
    bridge[method]!(...args);
    return;
  }
  if (window.parent !== window) {
    postMessageToParent(EMessageType.NB_CALLBACK, { method, args });
  }
};
